import {
  Button,
  makeStyles,
  Portal,
  shorthands,
  tokens,
  useModalAttributes,
} from "@fluentui/react-components";
import {
  ChevronDoubleLeft16Regular,
  ChevronDoubleRight16Regular,
  ChevronDown12Regular,
  CursorClick20Regular,
  Dismiss16Regular,
} from "@fluentui/react-icons";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { findStepElement, HELP_Z } from "./dom";
import { HelpStepJump } from "./HelpStepJump";
import { HelpTooltip } from "./HelpTooltip";
import { cardPosition, Rect, rectOf } from "./position";
import type { HelpLanguage, HelpStep, PageHelp } from "./types";
import { groupStepsBySection, localizedSection, localizedStep } from "./types";

/**
 * The guided tour: a spotlight over the real page, stepping through the steps that are on
 * screen right now (or only those inside the open dialog).
 *
 * Hand-rolled rather than pulling in driver.js/Shepherd: it needs a rectangle, a card and a few
 * buttons, it must stay inside the open dialog, keep Escape from closing that dialog, and offer
 * a step counter that is a button — the things tour libraries fight you on.
 *
 * It is not only linear: the step counter opens a searchable jump list, the section arrows skip
 * a whole block, and the card can hand over to "Point at a field".
 */

const CARD_WIDTH = 360;
/** Space to reserve for the card when deciding whether it fits below the target. */
const CARD_HEIGHT = 200;
const SPOTLIGHT_COLOR = "#f0a028";

const useStyles = makeStyles({
  blocker: {
    position: "fixed",
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    zIndex: HELP_Z.overlay - 1,
    backgroundColor: "transparent",
  },
  spotlight: {
    position: "fixed",
    zIndex: HELP_Z.overlay,
    ...shorthands.borderRadius("8px"),
    // One box with an enormous spread shadow: dims the whole page while leaving the target
    // itself untouched and readable.
    boxShadow: "0 0 0 9999px rgba(15, 23, 42, 0.55)",
    outlineWidth: "2px",
    outlineStyle: "solid",
    outlineColor: SPOTLIGHT_COLOR,
    outlineOffset: "2px",
    cursor: "pointer",
    transitionProperty: "top, left, width, height",
    transitionDuration: "120ms",
    transitionTimingFunction: "ease",
  },
  card: {
    position: "fixed",
    width: `${CARD_WIDTH}px`,
    zIndex: HELP_Z.card,
    boxSizing: "border-box",
    backgroundColor: tokens.colorNeutralBackground1,
    color: tokens.colorNeutralForeground1,
    ...shorthands.borderRadius("16px"),
    ...shorthands.padding("16px"),
    boxShadow: "0 12px 32px rgba(15, 23, 42, 0.28)",
    textAlign: "left",
    lineHeight: "normal",
  },
  topRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  counter: {
    minWidth: 0,
    height: "auto",
    ...shorthands.padding("2px", "4px"),
    marginLeft: "-4px",
    fontSize: "10px",
    fontWeight: tokens.fontWeightBold,
    letterSpacing: "1.2px",
    color: tokens.colorBrandForeground1,
  },
  topActions: {
    display: "flex",
    alignItems: "center",
    marginRight: "-6px",
  },
  section: {
    fontSize: "10px",
    fontWeight: tokens.fontWeightBold,
    letterSpacing: "0.8px",
    textTransform: "uppercase",
    color: tokens.colorNeutralForeground3,
    marginTop: "2px",
    whiteSpace: "nowrap",
    overflowX: "hidden",
    overflowY: "hidden",
    textOverflow: "ellipsis",
  },
  title: {
    fontSize: "14.5px",
    fontWeight: tokens.fontWeightSemibold,
    marginTop: "4px",
  },
  body: {
    fontSize: "13px",
    lineHeight: 1.55,
    color: tokens.colorNeutralForeground2,
    marginTop: "6px",
  },
  actions: {
    display: "flex",
    alignItems: "center",
    columnGap: "8px",
    marginTop: "14px",
  },
  inspect: {
    fontSize: "11.5px",
    marginLeft: "-6px",
    color: tokens.colorNeutralForeground2,
  },
  spacer: {
    flexGrow: 1,
  },
});

const isEditable = (target: EventTarget | null): boolean =>
  target instanceof HTMLElement &&
  (target.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName));

const isInTour = (target: EventTarget | null): boolean =>
  target instanceof Element && target.closest('[data-help-panel="tour"]') !== null;

interface HelpTourProps {
  steps: HelpStep[];
  /**
   * The subtree the tour is confined to — the open dialog, when the tour was started over one.
   * Every lookup is scoped to it, so an anchor that also exists on the page behind the backdrop
   * resolves to the copy the user is actually looking at.
   */
  scope?: HTMLElement | null;
  /** Where to enter the walk. The drawer's "start here" and the inspector both pick a step. */
  startIndex?: number;
  /** `PageHelp.sections` — the order the jump list and the section arrows follow. */
  sections?: string[];
  help: PageHelp;
  language: HelpLanguage;
  /** Hands over to "Point at a field"; the tour ends. */
  onInspect?: () => void;
  onClose: () => void;
}

export const HelpTour = ({
  steps,
  scope = null,
  startIndex = 0,
  sections,
  help,
  language,
  onInspect,
  onClose,
}: HelpTourProps) => {
  const styles = useStyles();
  const [index, setIndex] = useState(startIndex);
  const [rect, setRect] = useState<Rect | null>(null);
  const [jumpOpen, setJumpOpen] = useState(false);
  const counterRef = useRef<HTMLButtonElement>(null);
  /**
   * Read through a ref: the parent passes a fresh arrow every render, and as an effect dependency
   * it restarted the scroll-and-track effect on every header re-render.
   */
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  /**
   * An "always accessible" modalizer: a Fluent dialog under the tour traps focus with tabster,
   * which would otherwise pull focus back into the dialog while the reader types in the jump
   * list. The jump list renders inside the card so this one modalizer covers both.
   */
  const { modalAttributes } = useModalAttributes({ trapFocus: true, alwaysFocusable: true });

  const step = steps[index];
  const isLast = index === steps.length - 1;
  const spoken = step ? localizedStep(step, language) : null;

  const goTo = useCallback(
    (next: number): void => {
      if (next < 0 || next >= steps.length) return;
      setIndex(next);
    },
    [steps.length]
  );

  const groups = useMemo(() => groupStepsBySection(sections, steps), [sections, steps]);
  const groupIndex = groups.findIndex((group) => step && group.steps.includes(step));

  /** Skip to the first step of the neighbouring section — a block at a time, not a step. */
  const skipSection = (delta: number): void => {
    const target = groups[groupIndex + delta]?.steps[0];
    if (target) goTo(steps.indexOf(target));
  };

  // Measure before paint so the spotlight never renders one frame in the wrong place.
  useLayoutEffect(() => {
    if (!step) return;
    // A detached scope means the dialog was closed mid-tour; nothing inside it can be found, so
    // the tour walks off the end and closes rather than spotlighting the page behind it.
    const lookup = scope ?? document;
    const el = findStepElement(step.selector, lookup);
    if (!el) {
      if (isLast) closeRef.current();
      else setIndex((i) => i + 1);
      return;
    }
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    setRect(rectOf(el));

    // Smooth scroll lies for a while: follow the element until it settles, then on reflow.
    let frames = 0;
    let raf = 0;
    const track = (): void => {
      const current = findStepElement(step.selector, lookup);
      if (current) setRect(rectOf(current));
      if (++frames < 40) raf = window.requestAnimationFrame(track);
    };
    raf = window.requestAnimationFrame(track);

    const remeasure = (): void => {
      const current = findStepElement(step.selector, lookup);
      if (current) setRect(rectOf(current));
    };
    window.addEventListener("resize", remeasure);
    window.addEventListener("scroll", remeasure, true);
    return () => {
      window.cancelAnimationFrame(raf);
      window.removeEventListener("resize", remeasure);
      window.removeEventListener("scroll", remeasure, true);
    };
  }, [step, isLast, scope]);

  useEffect(() => {
    /**
     * Capture phase, and the handled keys are stopped here. A tour started over a dialog sits ON
     * TOP of it, and both listen for Escape — without capture, ending the tour also dismissed
     * the half-filled form it was explaining.
     *
     * While the jump list is open, Escape closes only the list and the arrows are left to the
     * caret in its search box.
     */
    const onKey = (e: KeyboardEvent): void => {
      if (jumpOpen) {
        if (e.key !== "Escape") return;
        e.stopPropagation();
        e.preventDefault();
        setJumpOpen(false);
        return;
      }
      if (e.key !== "Escape" && e.key !== "ArrowRight" && e.key !== "ArrowLeft") return;
      // Arrows in a text field outside the tour belong to its caret.
      if (e.key !== "Escape" && isEditable(e.target) && !isInTour(e.target)) return;
      e.stopPropagation();
      e.preventDefault();
      if (e.key === "Escape") closeRef.current();
      else if (e.key === "ArrowRight") goTo(index + 1);
      else goTo(index - 1);
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [goTo, index, jumpOpen]);

  if (!step || !rect) return null;
  const card = cardPosition(rect, { width: CARD_WIDTH, height: CARD_HEIGHT });
  const dismiss = (): void => (jumpOpen ? setJumpOpen(false) : onClose());

  return (
    <Portal>
      {/* The page is inert during the tour; a click outside the card ends it — or, while the
          jump list is open, only closes the list. */}
      <div className={styles.blocker} onClick={dismiss} data-help-panel="tour-blocker" />
      <div
        className={styles.spotlight}
        onClick={dismiss}
        data-help-panel="tour-spotlight"
        style={{
          top: rect.top - 4,
          left: rect.left - 4,
          width: rect.width + 8,
          height: rect.height + 8,
        }}
      />
      <div
        role="dialog"
        // Excluded from dialog detection: the tour's own card is never the work surface.
        data-help-panel="tour"
        aria-label={`Tour step ${index + 1} of ${steps.length}: ${spoken?.title ?? step.title}`}
        className={styles.card}
        style={{ top: card.top, left: card.left }}
        {...modalAttributes}
      >
        <div className={styles.topRow}>
          {/* The counter is the way OUT of the linear walk, so it has to look pressable. */}
          <Button
            ref={counterRef}
            appearance="subtle"
            size="small"
            className={styles.counter}
            icon={<ChevronDown12Regular />}
            iconPosition="after"
            onClick={() => setJumpOpen((v) => !v)}
            data-help="help-tour-jump"
            aria-label={`Step ${index + 1} of ${steps.length} — jump to another step`}
            aria-haspopup="dialog"
            aria-expanded={jumpOpen}
          >
            STEP {index + 1} OF {steps.length}
          </Button>

          <div className={styles.topActions}>
            {groups.length > 1 && (
              <>
                <HelpTooltip content="Previous section">
                  <Button
                    appearance="subtle"
                    size="small"
                    icon={<ChevronDoubleLeft16Regular />}
                    disabled={groupIndex <= 0}
                    onClick={() => skipSection(-1)}
                    aria-label="Previous section"
                  />
                </HelpTooltip>
                <HelpTooltip content="Next section">
                  <Button
                    appearance="subtle"
                    size="small"
                    icon={<ChevronDoubleRight16Regular />}
                    disabled={groupIndex < 0 || groupIndex >= groups.length - 1}
                    onClick={() => skipSection(1)}
                    aria-label="Next section"
                  />
                </HelpTooltip>
              </>
            )}
            <Button
              appearance="subtle"
              size="small"
              icon={<Dismiss16Regular />}
              onClick={onClose}
              aria-label="End tour"
            />
          </div>
        </div>

        {step.section && (
          <div className={styles.section}>{localizedSection(help, step.section, language)}</div>
        )}
        <div className={styles.title}>{spoken?.title ?? step.title}</div>
        <div className={styles.body}>{spoken?.body ?? step.body}</div>

        <div className={styles.actions}>
          {onInspect && (
            <Button
              appearance="transparent"
              size="small"
              icon={<CursorClick20Regular />}
              onClick={onInspect}
              data-help="help-tour-inspect"
              className={styles.inspect}
            >
              Point at a field
            </Button>
          )}
          <div className={styles.spacer} />
          <Button
            appearance="subtle"
            size="small"
            onClick={() => goTo(index - 1)}
            disabled={index === 0}
          >
            Back
          </Button>
          <Button
            appearance="primary"
            size="small"
            onClick={() => (isLast ? onClose() : goTo(index + 1))}
          >
            {isLast ? "Done" : "Next"}
          </Button>
        </div>

        {jumpOpen && (
          <HelpStepJump
            anchor={counterRef.current}
            steps={steps}
            sections={sections}
            help={help}
            language={language}
            currentIndex={index}
            onPick={goTo}
            onClose={() => setJumpOpen(false)}
          />
        )}
      </div>

    </Portal>
  );
};
